import styled from 'styled-components';
import Slider from '../../../new-components/Slider';
import DynamicCard from '../../../new-components/cards/DynamicCard';
import { getImagePath } from '../../../helpers/common';
import { handleCardClickEvent } from '../../../helpers/cards';
import { useSelector } from 'react-redux';
import { useTranslation } from 'next-i18next';
import { useContext, useEffect } from 'react';
import { SearchFiltersActionTypes } from '../../../reducers/search-filters/actions';
import { SearchFiltersTypes } from '../../../reducers/search-filters/reducer';
import {
  SearchContext,
  SearchContextInterface
} from '../../search-new/context/SearchContext';
import { useRouter } from 'next/router';
import { SearchViewTypes, FormTypes } from '../../../new-lib/constants/common';
import dynamic from 'next/dynamic';

const ContactFormContainer = dynamic(
  () => import('../../contact/components/organic-form/ContactFormContainer'),
  { ssr: false }
);

interface SoldOutProps {
  recommendedCompounds?: [];
  compound?: {
    id: number;
    name: string;
    nawy_organization_id: number | null;
    area: { id: string; name: string };
  };
}

const SimilarCompounds = ({ recommendedCompounds }: SoldOutProps) => {
  const { t } = useTranslation(['entity', 'common']);
  const getItemProps = (item: any, index: number) => {
    return {
      width: '100%',
      height: '100%',
      image: getImagePath(item.image),
      imageLayerData: {
        header: item.name,
        subHeader:
          item.properties_count + ' ' + t('properties', { ns: 'common' }),
        id: item.id
      },
      borderradius: '10px',
      linkTo: `/compound/${item.slug}`,
      onClick: () => {
        handleCardClickEvent({
          compound: item,
          eventAction: 'cmpnd_click',
          eventLocation: 'RecommendedSection',
          index: index,
          recommended: false,
          featureName: 'compound'
        });
      }
    };
  };

  const getSliderChildren = (items: any) => {
    return items.map((item: any, index: number) => {
      const itemProps = getItemProps(item, index);
      return <DynamicCard key={item.toString()} {...itemProps} />;
    });
  };

  const similarCompoundsProps = {
    slidesPerView: 'auto' as 'auto',
    spaceBetweenSlides: 8,
    slidesPerGroup: 1,
    children: getSliderChildren(recommendedCompounds),
    width: '200px',
    height: '200px'
  };

  return (
    <CompoundsContainer>
      <h3>{t('discover_similar_compounds')}</h3>
      <Slider {...similarCompoundsProps} />
    </CompoundsContainer>
  );
};

const SoldOutSection = ({ recommendedCompounds, compound }: SoldOutProps) => {
  const { query } = useRouter();
  const isMobile = useSelector((state: any) => state.app.isMobile);
  const { t } = useTranslation('entity');
  const { searchFiltersDispatch } = useContext(
    SearchContext
  ) as SearchContextInterface;

  // useEffect(() => {
  //   searchFiltersDispatch({
  //     type: SearchFiltersActionTypes.UPDATE_FILTERS_WITH_MULTISELECT,
  //     payload: {
  //       name: SearchFiltersTypes.COMPOUNDS,
  //       value: [compound?.id as any]
  //     }
  //   });
  // }, []);
  return (
    <SoldOutContainer sahelMap={query?.view === SearchViewTypes.MAP}>
      <div className="form-section">
        <div className="soldout-text">
          <h3>{t('no_properties_to_show', { ns: 'common' })}</h3>
          <p>{t('contact_for_buy_sell')}</p>
        </div>
        <div className="form-container">
          <ContactFormContainer
            renderedInputs={{
              buyerOrSeller: true,
              name: true,
              mobileNumber: true,
              message: true
            }}
            compound={compound}
            formSource={FormTypes.BUY_PROPERTY}
          />
        </div>
      </div>

      {recommendedCompounds && recommendedCompounds.length > 0 && (
        <>
          {isMobile && (
            <LineSeparator>
              <span className="line" />
              <span className="text">{t('or', { ns: 'common' })}</span>
              <span className="line" />
            </LineSeparator>
          )}
          <SimilarCompounds recommendedCompounds={recommendedCompounds} />
        </>
      )}
    </SoldOutContainer>
  );
};

const SoldOutContainer = styled.div<{ sahelMap?: boolean }>`
  width: 100vw;
  max-width: ${props => (props.sahelMap ? '100%' : 'unset')};
  margin-inline-start: ${props => (props.sahelMap ? '0' : '-16px')};
  background: linear-gradient(
    180deg,
    rgba(223, 232, 240, 0.37) 0%,
    rgba(223, 232, 240, 0.31) 100%
  );
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: ${props => (props.sahelMap ? '16px 8px' : '32px 16px')};

  .form-section {
    .soldout-text {
      text-align: center;
      margin-bottom: 16px;

      h3 {
        font-family: materialBold;
        font-size: 24px;
        color: ${props => props.theme.light.colors.primaryBlue};
        margin-bottom: 8px;
      }

      p {
        font-family: materialRegular;
        font-size: ${props => props.theme.font.sizes.s};
        color: ${props => props.theme.light.colors.black};
      }
    }

    .contact-form {
      background: none;
      padding: 0px;
      textarea {
        height: 82px;
      }
    }
  }

  @media screen and (min-width: ${props =>
      props.theme.breakPoints.desktop.min}) {
    width: 100%;
    margin-inline-start: 0px;
    border-radius: 8px;
    padding: ${props => (props.sahelMap ? '32px 8px' : '32px 64px')};
    background: url('/assets/images/banners/soldout-desktop.webp');

    .form-section {
      display: flex;
      flex-direction: ${props => (props.sahelMap ? 'column' : 'row')};
      align-items: center;
      justify-content: center;
      gap: 32px;

      .soldout-text {
        max-width: ${props => (props.sahelMap ? '100%' : '50%')};
        text-align: center;
        margin-bottom: 0px;

        h3 {
          font-size: ${props => (props.sahelMap ? '24px' : '34px')};
          margin-bottom: 15px;
        }

        p {
          font-size: ${props => props.theme.font.sizes.l};
        }
      }

      .contact-form {
        width: 374px;
        border-radius: 16px;
        background: #ffffff;
        box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.15);
        padding: 32px;
      }
    }
  }
`;

const CompoundsContainer = styled.div`
  h3 {
    font-family: materialBold;
    font-size: ${props => props.theme.font.sizes.l};
    color: ${props => props.theme.light.colors.primaryBlue};
    margin-bottom: 8px;
  }

  @media screen and (min-width: ${props =>
      props.theme.breakPoints.desktop.min}) {
    h3 {
      font-size: ${props => props.theme.font.sizes.xl};
      margin-bottom: 16px;
    }
  }
`;

const LineSeparator = styled.div`
  display: flex;
  align-items: center;

  .text {
    font-family: materialRegular;
    font-size: ${props => props.theme.font.sizes.xl};
    color: #555;
    padding: 0px 16px;
    text-transform: uppercase;
  }

  .line {
    width: 100%;
    height: 1px;
    background-color: #555;
  }
`;
export default SoldOutSection;
